import { element } from '@shared/easyfy.core';
import { StatsEventExporter } from '@shared/easyfy.events/exporters/eventExporter.stats';
import { Ga4EventExporter } from '@shared/easyfy.events/exporters/eventExporter.ga4';
import { EasyfyEventData, IDataMapper, IEasyfyEvent, IEasyfyEventExporter, IWorkerJob, ITrackableElement, ISessionStarted, IClientEventMetaData, IWorkerMessage } from '@shared/easyfy.events/easyfy.eventdata';
import { EasyfyEventLogger } from '@shared/easyfy.events/easyfy.event.devlogger';
import { FeatureNames, Features } from '@webServices/features.service';
import { KlaviyoEventExporter } from './exporters/eventExporter.klaviyo';
import { KevelEventExporter } from '@shared/easyfy.events/exporters/eventExporter.kevel';

export class ServiceNames {
  static Stats: string = "stats";
  static Ga4: string = "ga4";
  static Klaviyo: string = "klaviyo";
  static Kevel: string = "kevel";
}
export class EventNames {
  static PageView: string = "pageview";
  static ProductImpression: string = "productimpression";
  static ProductClick: string = "productclick";
  static ProductPageView: string = "productpageview";
  static BannerImpression = "bannerimpression";
  static BannerClick = "bannerclick";
  static ElementImpression = "elementimpression";
  static ElementClick = "elementclick";
  static AddToCart: string = "addtocart";
  static RemoveFromCart: string = "removefromcart";
  static QuantityChanged: string = "quantitychanged";
  static StartedCheckout: string = "startedcheckout";
  static CompletedCheckout: string = "completedcheckout";
  static SearchPerformed: string = "searchperformed";
  static ContinueToPayment: string = "continuetopayment";
  static SessionStarted: string = "sessionstarted";
  static LoggedIn: string = "loggedin";
  static LoggedOut: string = "loggedout";
}

export class Message {
  static FetchClientMetaData = "cmd_fetch_client_meta_data";
  static BlockStatsApi = "cmd_block_stats_api";
}

export class LocalStorageKeys {
  static SessionTimer = "e_sessionTimer";
  static SessionId = "e_sessionId";
  static ClientId = "e_clientId";
  static UserId = "e_userId";
  static LastCheckedUserId = "e_lastCheckedUserId";
  static IsTablet = "e_tablet";
  static IsMobilePhone = "e_mobilephone";
  static IsDekstop = "e_desktop";
  static UserIdCookieKey = "e_userIdCookieKey";
  static IPAddress = "e_ipAddress";
  static StatsIsBlocked = "e_statsServiceBlocked";
  static StatsEventsThresholdRemoved = "e_StatsEventsTresholdRemoved"
  static StatsEventsTransitionObj = "e_statsEventsTransitionObj"
}

namespace Easyfy.Modules {

  export class EasyfyEventHandler {
    private eventExporters: Map<string, IEasyfyEventExporter> = new Map<string, IEasyfyEventExporter>();
    private observedElements: string[] = [];
    private intersectionObserver: IntersectionObserver;
    private bannerIntersectionObserver: IntersectionObserver;
    private mutationObserver: MutationObserver;
    public static instance: EasyfyEventHandler;
    private featureFlag: boolean;
    private initiated: boolean = false;

    constructor(eventMappers: Map<string, IDataMapper>) {
      EasyfyEventHandler.instance = this;
      EasyfyEventHandler.instance.featureFlag = Features.FeatureIsActive(FeatureNames.EventTracking);
      window.EasyfyEventLayerInstance = this;

      if (!EasyfyEventHandler.instance.featureFlag) return;

      EasyfyEventData.GetAllServices().forEach(service => {
        var eventExporter: IEasyfyEventExporter;
        switch (service.name.toLowerCase()) {
          case ServiceNames.Stats: {
            eventExporter = new StatsEventExporter();
            break;
          }
          case ServiceNames.Klaviyo: {
            eventExporter = new KlaviyoEventExporter();
            break;
          }
          case ServiceNames.Ga4: {
            eventExporter = new Ga4EventExporter();
            break;
          }
          case ServiceNames.Kevel: {
            eventExporter = new KevelEventExporter();
            break;
          }
        }
        if (eventExporter) {
          eventExporter.WithMapper(eventMappers.get(service.name));
          this.eventExporters.set(service.name, eventExporter);
        }
      })

      EasyfyEventLogger.Log({
        type: "updateeventlayerstatus",
      });

      EasyfyEventHandler.CheckSessionStart().then(() => {
        EasyfyEventHandler.RunEvents().then(() => {
          if (EasyfyEventData.ShouldRegisterProductImpressions()) {
            EasyfyEventHandler.InitProductIntersectionObserver();
            EasyfyEventHandler.InitMutationObserver();
          }
          if (EasyfyEventData.ShouldRegisterBannerImpressions()) {
            EasyfyEventHandler.InitBannerIntersectionObserver();
          }
          EasyfyEventHandler.InitElementIntersectionObserver();
          window.addEventListener('click', EasyfyEventHandler.ClickEventChecker);
          EasyfyEventHandler.instance.initiated = true;
        });
      });
    };

    static async RunEvents(): Promise<void> {
      return new Promise<void>(async (resolve) => {
        var inlineScript = element('[data-container="easyfy-preload-tracking-events"]');
        if (inlineScript) {
          inlineScript.remove();
        }

        if (!EasyfyEventData.HasEvents()) {
          resolve();
          return;
        }
        for await (const event of EasyfyEventData.Events()) {
          await EasyfyEventHandler.RunEvent(event);
        };

        EasyfyEventData.ClearEvents();
        resolve();
      });
    };

    static async CheckSessionStart(): Promise<void> {
      return new Promise(async (resolve) => {
        if (EasyfyEventData.ServiceIsActive(ServiceNames.Stats) !== true) {
          resolve();
          return;
        }
        var sessionTimer = localStorage.getItem(LocalStorageKeys.SessionTimer);
        if (!sessionTimer) {
          localStorage.setItem(LocalStorageKeys.SessionTimer, new Date(Date.now()).toUTCString());
          sessionTimer = localStorage.getItem(LocalStorageKeys.SessionTimer);
          await EasyfyEventHandler.FetchClientMetaData();
          await EasyfyEventHandler.SendStartSessionEvent();
          resolve();
        }

        var checkTime = new Date(sessionTimer);
        var now = new Date(Date.now());

        var diff = (checkTime.getTime() - now.getTime()) / 1000;
        diff /= 60;
        var minutesBetween = Math.abs(Math.round(diff));

        if (minutesBetween >= 20) {
          var oldSessionId = localStorage.getItem(LocalStorageKeys.SessionId);
          await EasyfyEventHandler.FetchClientMetaData();
          var newSessionId = localStorage.getItem(LocalStorageKeys.SessionId);
          if (oldSessionId != newSessionId) {
            await EasyfyEventHandler.SendStartSessionEvent();
          }
        }

        localStorage.setItem(LocalStorageKeys.SessionTimer, new Date(Date.now()).toUTCString());

        resolve();
      });
    }

    static async FetchClientMetaData(): Promise<boolean> {
      return new Promise(async (resolve) => {

        EasyfyEventData.SetStatsApiBlock(false);

        const response = await fetch("/api/stats/clientmetadata/");

        if (!response.ok) {
          if (response.status == 428 || response.status == 404) {
            EasyfyEventData.SetStatsApiBlock(true);
          }

          localStorage.setItem(LocalStorageKeys.SessionId, null);
          localStorage.setItem(LocalStorageKeys.ClientId, null);
          localStorage.setItem(LocalStorageKeys.UserId, null);
          localStorage.setItem(LocalStorageKeys.IsDekstop, null);
          localStorage.setItem(LocalStorageKeys.IsMobilePhone, null);
          localStorage.setItem(LocalStorageKeys.IsTablet, null);
          localStorage.setItem(LocalStorageKeys.UserIdCookieKey, null);
          localStorage.setItem(LocalStorageKeys.IPAddress, null);

          resolve(false);
          return;
        }

        var clientMetaData = await response.json()

        localStorage.setItem(LocalStorageKeys.SessionId, clientMetaData.sessionId);
        localStorage.setItem(LocalStorageKeys.ClientId, clientMetaData.clientId);
        localStorage.setItem(LocalStorageKeys.UserId, clientMetaData.userId);
        localStorage.setItem(LocalStorageKeys.IsDekstop, clientMetaData.isDesktop);
        localStorage.setItem(LocalStorageKeys.IsMobilePhone, clientMetaData.isMobilePhone);
        localStorage.setItem(LocalStorageKeys.IsTablet, clientMetaData.isTablet);
        localStorage.setItem(LocalStorageKeys.UserIdCookieKey, clientMetaData.userIdCookieKey);
        localStorage.setItem(LocalStorageKeys.IPAddress, clientMetaData.ipAddress);


        var data: IClientEventMetaData = {
          ClientId: clientMetaData.clientId,
          SessionId: clientMetaData.sessionId,
          UserId: clientMetaData.userId,
          IsDesktop: clientMetaData.isDesktop,
          IsMobilePhone: clientMetaData.isMobilePhone,
          IsTablet: clientMetaData.isTablet,
          IPAddress: clientMetaData.ipAddress
        }
        resolve(true);
      });
    }

    static BlockStatsApi(): void {
      EasyfyEventData.SetStatsApiBlock(true);
    }

    static HandleWorkerMessage(message) {
      var typedMessage = message.data as IWorkerMessage
      switch (typedMessage.Message) {
        case Message.FetchClientMetaData: {
          EasyfyEventHandler.FetchClientMetaData()
          break;
        }
        case Message.BlockStatsApi: {
          EasyfyEventHandler.BlockStatsApi()
          break;
        }
      }
    }

    static SendStartSessionEvent(): Promise<void> {
      return new Promise<void>(async (resolve) => {
        var clientMetaData: IClientEventMetaData = EasyfyEventData.GetClientEventMetaData();
        var clientData: ISessionStarted = {
          Culture: "not-set-yet", //todo -> ? 
          Currency: EasyfyEventData.SiteInfo().currencyCode,
          IsDesktop: clientMetaData.IsDesktop,
          IsMobilePhone: clientMetaData.IsMobilePhone,
          IsTablet: clientMetaData.IsTablet,
          RecurringUser: EasyfyEventData.IsRecurringUser(true),
          Referrer: document.referrer,
          Path: window.location.pathname,
          UserAgent: window.navigator.userAgent,
          ClientId: clientMetaData.ClientId,
          SessionId: clientMetaData.SessionId,
          UserId: clientMetaData.UserId,
          VisitorId: clientMetaData.UserId
        };

        var sessionStartedEvent: IEasyfyEvent = {
          eventName: EventNames.SessionStarted,
          data: clientData
        };

        await EasyfyEventHandler.RunEvent(sessionStartedEvent);
        resolve();
      });
    }

    static InitMutationObserver(): void {
      let options = {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      };

      let targets = document.querySelectorAll('[data-track-list], [data-container="search-result"]');
      EasyfyEventHandler.instance.mutationObserver = new MutationObserver(this.ElementMutated);
      targets.forEach(target => EasyfyEventHandler.instance.mutationObserver.observe(target, options));
    }

    static ElementMutated(mutations: MutationRecord[]) {
      mutations.forEach(mutation => {
        EasyfyEventHandler.RebindObserveProducts();
      });
    }

    static BannerElementMutated(mutations: MutationRecord[]) {
      mutations.forEach(mutation => {
        EasyfyEventHandler.RebindObserveBanners();
      });
    }

    static InitProductIntersectionObserver(): void {
      let options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.intersectionObserver = new IntersectionObserver(this.ProductCardObserved, options);
      let targets = document.querySelectorAll('[data-track-product]');
      targets.forEach(target => EasyfyEventHandler.instance.intersectionObserver.observe(target));
    }

    static InitBannerIntersectionObserver(): void {
      let options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.bannerIntersectionObserver = new IntersectionObserver(this.BannerElementObserved, options);
      let bannerTargets = document.querySelectorAll('[data-track-banner]');
      bannerTargets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static InitElementIntersectionObserver(): void {
      let options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      EasyfyEventHandler.instance.bannerIntersectionObserver = new IntersectionObserver(this.TrackableElementObserved, options);
      let bannerTargets = document.querySelectorAll('[data-track-element]');
      bannerTargets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static RebindObserveProducts() {
      EasyfyEventHandler.instance.intersectionObserver.disconnect();
      let targets = document.querySelectorAll('[data-track-product]');
      targets.forEach(target => EasyfyEventHandler.instance.intersectionObserver.observe(target));
    }

    static RebindObserveBanners() {
      EasyfyEventHandler.instance.bannerIntersectionObserver.disconnect();
      let targets = document.querySelectorAll('[data-track-banner]');
      targets.forEach(target => EasyfyEventHandler.instance.bannerIntersectionObserver.observe(target));
    }

    static ProductCardObserved(entries: IntersectionObserverEntry[], observer: any) {
      var eventProductIds: string[] = [];
      var eventProductSalesUnitCodes: string[] = [];
      var listIds: string[] = [];
      var listNames: string[] = [];
      var promotedProducts: boolean[] = [];
      var propertyCollection: Map<string, any>[] = [];


      entries.forEach(entry => {
        if (entry.isIntersecting) {
          EasyfyEventHandler.instance.intersectionObserver.unobserve(entry.target);
          var elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression").toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;

          // points to a product but could appear multiple times on a page
          var productIdentifier = entry.target.getAttribute("data-product-event-id");
          // id of product card element, always unique
          var elementIdentifier = entry.target.getAttribute("data-element-event-id");
          var defaultSalesUnitCode = entry.target.getAttribute("data-default-sales-unit-code");
          var isPromotedProduct = entry.target.getAttribute("data-event-property-is-promotion")?.toLowerCase() === 'true';
   

          if (productIdentifier && elementIdentifier) {
            if (!EasyfyEventHandler.instance.observedElements.includes(elementIdentifier)) {
              EasyfyEventHandler.instance.observedElements.push(elementIdentifier);
              EasyfyEventLogger.Log({
                type: "productimpression",
                message: (`Product '${productIdentifier}' with SalesCodeUnit '${defaultSalesUnitCode}' seen`),
                target: entry.target
              });
              eventProductIds.push(productIdentifier);
              eventProductSalesUnitCodes.push(defaultSalesUnitCode);
              promotedProducts.push(isPromotedProduct);
              var node = entry.target;
              while (true) {
                node = node.parentElement;
                if (node.hasAttribute("data-track-list")) {
                  let listId = node.getAttribute("data-list-id");
                  let listName = node.getAttribute("data-list-name");
                  if (listId || listName) {
                    listId && listIds.push(listId);
                    listName && listNames.push(listName);
                    break;
                  }
                }
                else if (node.nodeName == 'BODY') {
                  listIds.push(null);
                  listNames.push(null);
                  break;
                }
              }
              const properties = EasyfyEventHandler.GetProperties(entry.target)
              propertyCollection.push(properties)
            }
          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Product impression failed: missing event product ID"
            });
          }
        }
      });

      if (eventProductIds.length > 0) {
        let event: IEasyfyEvent = {
          eventName: EventNames.ProductImpression,
          data: {
            productIdentifiers: eventProductIds,
            salesUnitCodes: eventProductSalesUnitCodes,
            listIds: listIds,
            listNames: listNames,
            promotedProducts,
            propertyCollection
          }
        }
        EasyfyEventHandler.RunEvent(event);
      }
    }

    static BannerElementObserved(entries: IntersectionObserverEntry[], observer: any) {
      var bannerIds: string[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          EasyfyEventHandler.instance.bannerIntersectionObserver.unobserve(entry.target);
          var elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression").toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;
          var bannerIdentifier = entry.target.getAttribute("data-banner-event-id");
          if (bannerIdentifier) {
            if (!EasyfyEventHandler.instance.observedElements.includes(bannerIdentifier)) {
              EasyfyEventHandler.instance.observedElements.push(bannerIdentifier);
              EasyfyEventLogger.Log({
                type: "bannerimpression",
                message: ("Banner '" + bannerIdentifier + "' seen")
              });
              bannerIds.push(bannerIdentifier);
            }
          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Banner impression failed: missing event banner ID"
            });
          }
        }
      });

      if (bannerIds.length > 0) {
        bannerIds.forEach(id => {
          let event: IEasyfyEvent = {
            eventName: EventNames.BannerImpression,
            data: { bannerIdentifier: id }
          }
          EasyfyEventHandler.RunEvent(event);
        });
      }
    }

    static TrackableElementObserved(entries: IntersectionObserverEntry[], observer: any) {
      var elements: ITrackableElement[] = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          var elementIsRegisteredForImpression = entry.target.getAttribute("data-track-impression")?.toLowerCase() === 'true';
          if (!elementIsRegisteredForImpression) return;

          var elementIdentifier = entry.target.getAttribute("data-event-id");
          if (elementIdentifier) {
            var multiObserve = entry.target.getAttribute("data-multi-observe")?.toLowerCase() === 'true';
            if (multiObserve == false) {
              EasyfyEventHandler.instance.bannerIntersectionObserver.unobserve(entry.target);
              if (!EasyfyEventHandler.instance.observedElements.includes(elementIdentifier)) {
                EasyfyEventHandler.instance.observedElements.push(elementIdentifier);
              }
            }

            var trackType = entry.target.getAttribute("data-track-type");
            var properties = new Map<string, any>();

            for (const attribute of entry.target.getAttributeNames()) {
              if (attribute.startsWith("data-event-property-")) {
                const value = entry.target.getAttribute(attribute);
                var key = attribute.replace("data-event-property-", "");
                properties.set(key, value);
              }
            }

            elements.push({
              Id: elementIdentifier,
              Type: trackType,
              Properties: properties
            });

            EasyfyEventLogger.Log({
              type: "elementimpression",
              message: ("Element '" + elementIdentifier + "' seen")
            });

          }
          else {
            EasyfyEventLogger.Log({
              type: "error",
              message: "Element impression failed: missing event ID on element"
            });
          }
        }
      });

      if (elements.length > 0) {
        elements.forEach(element => {
          let event: IEasyfyEvent = {
            eventName: EventNames.ElementImpression,
            data: element
          }
          EasyfyEventHandler.RunEvent(event);
        });
      }
    }

    static ClickEventChecker(event: any) {
      if (event.target.closest('A') != null) {
        var node = event.target.closest('A');
        var targetUrl = node.href;
        if (node.closest('[data-track-product]') != null) {
          EasyfyEventHandler.ClickEventProduct(event, node, targetUrl);
          return;
        }
        else if (node.closest('[data-track-banner]') != null) {
          EasyfyEventHandler.ClickEventBanner(event, node, targetUrl);
          return;
        }
        else if (node.closest('[data-track-element]') != null) {
          EasyfyEventHandler.ClickEventElement(event, node, targetUrl);
          return;
        }
      }
      else {
        var node = event.target;
        if (node.closest('[data-track-element]') != null) {
          EasyfyEventHandler.ClickEventElement(event, node);
          return;
        }
      }
    }

    static ClickEventProduct(event: any, node: any, targetUrl: any) {
      node = node.closest('[data-track-product]');
      var elementIsRegisteredForClick = node.getAttribute("data-track-click").toLowerCase() === 'true';
      if (elementIsRegisteredForClick) {
        event.stopPropagation();
        event.preventDefault();

        var defaultSalesUnitCode = node.getAttribute("data-default-sales-unit-code");
        var productId = node.getAttribute('data-product-event-id');
        var isPromotedProduct = node.getAttribute("data-event-property-is-promotion")?.toLowerCase() === 'true';


        var propertyCollection = EasyfyEventHandler.GetProperties(node);


        if (productId) {
          var listName
          var listId
          while (true) {
            node = node.parentElement;
            if (!node) {
              break;
            }
            if (node.hasAttribute("data-track-list")) {
              listName = node.getAttribute("data-list-name");
              listId = node.getAttribute("data-list-id");
              break;
            }
            else if (node.nodeName == 'body') {
              listName = null;
              listId = null;
              break;
            }
          }

          let event: IEasyfyEvent = {
            eventName: EventNames.ProductClick,
            data: {
              productIdentifier: productId,
              salesUnitCode: defaultSalesUnitCode,
              listName: listName,
              listId: listId,
              isPromotedProduct: isPromotedProduct,
              propertyCollection
            }
          }
          EasyfyEventHandler.RunEvent(event);
          window.location = targetUrl;
        }
        else {
          //Don't block links
          window.location = targetUrl;
        }
      }
    }

    static ClickEventBanner(event: any, node: any, targetUrl: any) {
      node = node.closest('[data-track-banner]');
      var elementIsRegisteredForClick = node.getAttribute("data-track-click").toLowerCase() === 'true';
      if (elementIsRegisteredForClick) {
        event.stopPropagation();
        event.preventDefault();

        var bannerId = node.getAttribute('data-banner-event-id');
        if (bannerId) {
          let event: IEasyfyEvent = {
            eventName: EventNames.BannerClick,
            data: {
              bannerIdentifier: bannerId,
            }
          }
          EasyfyEventHandler.RunEvent(event);
          window.location = targetUrl;
        }
        else {
          //Don't block links
          window.location = targetUrl;
        }
      }
    }

    static ClickEventElement(event: any, node: any, targetUrl?: any) {
      node = node.closest('[data-track-element]');
      var elementIsRegisteredForClick = node.getAttribute("data-track-click")?.toLowerCase() === 'true' ?? false;
      if (elementIsRegisteredForClick) {

        if (targetUrl) {
          event.stopPropagation();
          event.preventDefault();
        }

        var elementId = node.getAttribute('data-event-id');

        if (elementId) {
          var trackType = node.getAttribute('data-track-type');
          var properties = new Map<string, any>();

          for (const attribute of node.getAttributeNames()) {
            if (attribute.startsWith("data-event-property-")) {
              const value = node.getAttribute(attribute);
              var key = attribute.replace("data-event-property-", "");
              properties.set(key, value);
            }
          }

          var data: ITrackableElement = {
            Id: elementId,
            Type: trackType,
            Properties: properties
          }

          let event: IEasyfyEvent = {
            eventName: EventNames.ElementClick,
            data
          }

          EasyfyEventHandler.RunEvent(event);

          const singleClick = node.dataset['singleClick'];
          if (singleClick) node.removeAttribute('data-track-element');

          if (targetUrl) {
            window.location = targetUrl;
          }
        }
        else if (targetUrl) {
          //Don't block links
          window.location = targetUrl;
        }
      }
    }

    static GetCurrentInstance(): EasyfyEventHandler {
      var instance: EasyfyEventHandler;
      if (EasyfyEventHandler.instance) {
        instance = EasyfyEventHandler.instance
      }
      else if (window.EasyfyEventLayerInstance) {
        instance = window.EasyfyEventLayerInstance as EasyfyEventHandler;
      }

      return instance;
    }

    static RunEvent(event: IEasyfyEvent): Promise<void> {
      return new Promise<void>(async (resolve) => {
        var dispatchedToServices = [];
        var errors = new Map<string, string>();
        for await (const service of EasyfyEventData.GetAllServices()) {
          if (EasyfyEventData.ConsumesEvent(service.name, event.eventName)) {
            try {
              var instance = EasyfyEventHandler.GetCurrentInstance();

              await instance.eventExporters.get(service.name)?.RegisterEvent(event);
              dispatchedToServices.push(service.name);

            }
            catch (error) {
              errors.set(service.name, event.eventName);
              console.warn(error);
            }
          }
        };

        EasyfyEventLogger.BroadcastToLogger(event);

        if (dispatchedToServices.length > 0) {
          EasyfyEventLogger.Log({ type: "event", message: `Event '${event.eventName}' dispatched to: ${dispatchedToServices.join(', ')}` });
        } else {
          EasyfyEventLogger.Log({ type: "event", message: `Event '${event.eventName}' fired but not dispatched to any services` });
        }
        errors.forEach((value, key) => {
          EasyfyEventLogger.Log({ type: "error", message: `Error when trying to dispatch event '${value}' to: ${key}` });
        });

        resolve();
      });
    }

    static async RegisterResponseData(responseData: any): Promise<void> {
      return new Promise(async (resolve) => {
        var instance = EasyfyEventHandler.GetCurrentInstance();
        if (instance?.initiated) {
          await EasyfyEventHandler.CheckSessionStart();
          if (responseData?.data?.eventLayer) {
            EasyfyEventLogger.Log({
              message: "Event layer data added from response",
            });

            EasyfyEventData.AddDataToEventLayer(responseData.data.eventLayer);

            EasyfyEventLogger.Log({
              type: "updateeventlayerstatus",
            });
            await EasyfyEventHandler.RunEvents();
          }
        }
        resolve();
      });
    }

    static GetCookie(cookieName) {
      let cookie = {};
      document.cookie.split(';').forEach(function (el) {
        let [key, value] = el.split('=');
        cookie[key.trim()] = value;
      })
      return cookie[cookieName];
    }

    static GetProperties(element: Element) {
      const properties = new Map<string, any>();

      if (!element || typeof element.getAttributeNames !== 'function') {
        return properties;
      }

      for (const attribute of element.getAttributeNames()) {
        if (attribute.startsWith("data-event-property-")) {
          const value = element.getAttribute(attribute);
          if (value !== null) {
            const key = attribute.replace("data-event-property-", "");
            properties.set(key, value);
          }
        }
      }

      return properties;
    }
  }
}

export default Easyfy.Modules;
